import { API, Endpoints, PageModel, Project } from "@brg/api";
import { LayoutConst, useModal } from "core";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CreateLinkTo } from "shared/constants/linkTo";
import styled, { createGlobalStyle } from 'styled-components';
import { TableWrapper, TdAuthor, TdDel, TdSum, TdTitle } from "./ShortListWinnerProjectsEdit";
import { FlexBox, useBreakpoint } from "@brg/ui";
import { DefaultButton } from "shared/pages/teams/styles";
import DeleteIcon from "shared/icons/DeleteIcon";
import { ReactComponent as PlusSvg } from "../../../../../shared/pages/projectEdit/close.svg";
import { MODAL_TYPE } from "shared/modals/selectShortList/SelectShortList";
import { CompetitionModalTabs } from "./components/CompetitionModalTabs";

export const SortableShortList = styled.div<{ mobileColumns?: number }>`

  .short-list, .sortable-short-list {
    grid-gap: 8px;
  }

  .sortable-short-list {
    user-select: none;
  }

  .sortable-short-list-item, .short-list-item {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: black;
    margin: 8px;
    height: 40px;
    background-color: #F2F2F2;
    flex-direction: row;
  }


  .sortable-short-list-item {
    cursor: grab;
    height: 100px;

    input {
      width: 35px !important;
      background-color: #F2F2F2;
    }
  }
`;

const GlobalDragStyle = createGlobalStyle`

  .sortable-short-list-item {
    background: transparent !important;
    height: 42px;
    border-bottom: 12px;
  }

  .sortable-short-list-item-dragged {
    // width: 500px;
    display: flex;
    align-items: center;
    justify-content: start;
    //   padding: 0 12px;
    gap: 8px;
    border: none !important;
    background: #343434 !important;
    border-collapse: collapse;

    td {
      height: 100%;
      display: flex;
      align-items: center;
      padding: 0 !important;

      // a, .brg-input__single-value {
      //   color: rgba(144, 144, 144, 1);
      // }

      min-height: 42px;
      max-height: 42px;
      border: none !important;
      border-color: transparent !important;
      border-radius: 3px;
      border-spacing: 0px;

      .td-content {
        display: flex;
        justify-content: flex-start;
        place-items: center;
        width: 100%;
        border: 0 !important;
        margin: 0 !important;
        border-spacing: 0 !important;

        &, & div, & a {
          color: inherit !important;
        }

        &.td-sum {
          max-width: 105px;
          width: 105px;
          border: none !important;
          margin-left: -1px;
        }

        &.td-title {
          width: 402px;
          border: none !important;
          @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
            width: 285px;
          }
        }

        &.td-author {
          width: 396px;
          border: none !important;

          @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
            width: 285px;
          }
        }

        &:not(.td-delete) {
          padding: 10px 16px;
          height: auto;
          min-height: 42px;
          background-color: ${({ theme }: any) => theme.competition.table.input.bg} !important;
        }

        &.td-delete {
          max-width: 19px;
          border: 0 ! important;
          padding: 0 !important;
          // margin-left: -9px;
          svg {
            max-width: 17px;
          }
        }

        @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
          height: 36px;
        }
      }

      .td-dropdown {
        flex-flow: row;
        padding: 0 !important;
        height: 42px !important;
        width: 45px;

        .brg-input__control {
          background: inherit;
        }

        @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
          height: 36px;
        }
      }

      *:not(th) {
        font-size: 17px;
        line-height: 22px;
        font-weight: 400;
        letter-spacing: -0.02em;

        @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
          font-size: 13px;
          line-height: 16px;
        }
      }
    }

    ${TdTitle}, ${TdAuthor} {
      width: 100%;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
      width: calc(100vw - 20px);
    }
  }
`;

interface ShortListProjectsEditProps {
  competitionId: string;
  projectIds: string[];
  onSetProjects: (projectIds: string[]) => void;
  onSort?: (oldIndex: number, newIndex: number) => void;
  draggable?: boolean;
};

export const ShortListProjectsEdit: FC<ShortListProjectsEditProps> = ({
  competitionId,
  projectIds,
  onSetProjects,
  onSort
}) => {
  const { i18n, t } = useTranslation();
  const modal = useModal();
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
  const isTablet = useBreakpoint(LayoutConst.screen.md);

  const [items, setItems] = useState<Project[]>([]);

  useEffect(() => {
    if (projectIds.length) {
      API.request()
        .post()
        .endpoint(Endpoints.geograffee)
        .url(`api/project/GetProjectsList`)
        .body({
          projectIds: projectIds,
          projectFilter: 1,
          isLite: true,
          isContextIgnored: true
        })
        .send()
        .then(v => v.json())
        .then((v) => API.toLang<PageModel<Project>>(v, i18n.language))
        .then((v) => {
          if (v?.data?.length) {
            const prjs = [...v.data];
            prjs.sort((a: Project, b: Project): number => {
              const index_a = projectIds.indexOf(a.projectId);
              const index_b = projectIds.indexOf(b.projectId);

              if (index_a < index_b) {
                return -1;
              }
              if (index_a > index_b) {
                return 1;
              }
              // a must be equal to b
              return 0;
            });
            setItems(prjs);
          } else {
            setItems([]);
          }
        });
    } else {
      setItems([]);
    }
  }, [projectIds, i18n]);

  const tabsComponent = useMemo(() => (<CompetitionModalTabs/>), []);

  const onSelectClick = useCallback(() => {
    modal.show('select-short-list', {
      competitionId: competitionId,
      projectIds: projectIds,
      onSetProjects: onSetProjects,
      sortingMethods: [1, 2, 13],
      sendText: t('competitions_edit.btns.save'),
      searchData: { id: competitionId },
      modalType: MODAL_TYPE.finalist,
      tabs: tabsComponent
    });
  }, [modal, competitionId, projectIds, onSetProjects, tabsComponent, t]);

  const onDelete = useCallback((index: number, position?: number) => {
    const temp = [...projectIds];
    temp.splice(index, 1);
    onSetProjects(temp);
  }, [projectIds, onSetProjects])

  if (!projectIds?.length) {
    return (
      <DefaultButton onClick={onSelectClick} isCompetitionEdit hollow>{t('competition_edit.choose')}</DefaultButton>
    )
  }

  return (
    <FlexBox direction="column" directionMobile="column" gyMobile={12} gy={12}>
      <GlobalDragStyle/>
      <TableWrapper width={'100%'}>
        <thead>
        <tr>
          <th style={{ width: '45%' }} className='project-name'>
            Проект
          </th>
          <th style={{ width: '45%' }} className='author-name'>Автор</th>
          <th style={{ width: '10%' }} className='vertical-th jury-sum'>Балл</th>
          <th style={{ width: '10%' }} className='vertical-th jury-sum'></th>
        </tr>
        </thead>
        {
          <tbody className="short-list">
          {items?.map((value, idx) => (
            <tr key={value.projectId} className="short-list-item">
              <TdTitle>
                <div className="td-content">
                  <Link to={CreateLinkTo.projectNew(value)} target={"_blank"}>{(!!value?.title ? value.title : value?.additionalContent?.title?.text) as any}</Link>&nbsp;&nbsp;
                </div>
              </TdTitle>
              <TdAuthor>
                <div className="td-content">
                  <Link to={CreateLinkTo.designerNew(value.author)}
                        target={"_blank"}>{value.author.name + ' ' + value.author.surName}
                  </Link>&nbsp;&nbsp;
                </div>
              </TdAuthor>
              <TdSum>
                <div className="td-content" style={{ justifyContent: 'center' }}>
                  {value.competitonsGPA?.find(x => x.competitionId === competitionId)?.competitionGPA ?? '0'/*t('competition_edit.no_points')*/}
                </div>
              </TdSum>
              {!isMobile && (
                <TdDel style={{ width: '24px !important' }}>
                  <div className="td-content td-delete" style={{ cursor: 'pointer' }} onClick={() => onDelete(idx)}>
                    <DeleteIcon/>
                  </div>
                </TdDel>
              )}

            </tr>
          ))}
          </tbody>
        }

      </TableWrapper>
      {!!items?.length && <div>
        <DefaultButton style={{ marginBottom: isTablet ? '24px' : 0 }} isCompetitionEdit hollow onClick={onSelectClick}><PlusSvg/>{t('competition_edit.add_final_place')}
        </DefaultButton>
      </div>}
    </FlexBox>
  );
};import { API, Endpoints, PageModel, Project } from "@brg/api";
import { LayoutConst, useModal } from "core";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CreateLinkTo } from "shared/constants/linkTo";
import styled, { createGlobalStyle } from 'styled-components';
import { TableWrapper, TdAuthor, TdDel, TdSum, TdTitle } from "./ShortListWinnerProjectsEdit";
import { FlexBox, useBreakpoint } from "@brg/ui";
import { DefaultButton } from "shared/pages/teams/styles";
import DeleteIcon from "shared/icons/DeleteIcon";
import { ReactComponent as PlusSvg } from "../../../../../shared/pages/projectEdit/close.svg";
import { MODAL_TYPE } from "shared/modals/selectShortList/SelectShortList";
import { CompetitionModalTabs } from "./components/CompetitionModalTabs";

export const SortableShortList = styled.div<{ mobileColumns?: number }>`

  .short-list, .sortable-short-list {
    grid-gap: 8px;
  }

  .sortable-short-list {
    user-select: none;
  }

  .sortable-short-list-item, .short-list-item {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: black;
    margin: 8px;
    height: 40px;
    background-color: #F2F2F2;
    flex-direction: row;
  }


  .sortable-short-list-item {
    cursor: grab;
    height: 100px;

    input {
      width: 35px !important;
      background-color: #F2F2F2;
    }
  }
`;

const GlobalDragStyle = createGlobalStyle`

  .sortable-short-list-item {
    background: transparent !important;
    height: 42px;
    border-bottom: 12px;
  }

  .sortable-short-list-item-dragged {
    // width: 500px;
    display: flex;
    align-items: center;
    justify-content: start;
    //   padding: 0 12px;
    gap: 8px;
    border: none !important;
    background: #343434 !important;
    border-collapse: collapse;

    td {
      height: 100%;
      display: flex;
      align-items: center;
      padding: 0 !important;

      // a, .brg-input__single-value {
      //   color: rgba(144, 144, 144, 1);
      // }

      min-height: 42px;
      max-height: 42px;
      border: none !important;
      border-color: transparent !important;
      border-radius: 3px;
      border-spacing: 0px;

      .td-content {
        display: flex;
        justify-content: flex-start;
        place-items: center;
        width: 100%;
        border: 0 !important;
        margin: 0 !important;
        border-spacing: 0 !important;

        &, & div, & a {
          color: inherit !important;
        }

        &.td-sum {
          max-width: 105px;
          width: 105px;
          border: none !important;
          margin-left: -1px;
        }

        &.td-title {
          width: 402px;
          border: none !important;
          @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
            width: 285px;
          }
        }

        &.td-author {
          width: 396px;
          border: none !important;

          @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
            width: 285px;
          }
        }

        &:not(.td-delete) {
          padding: 10px 16px;
          height: auto;
          min-height: 42px;
          background-color: ${({ theme }: any) => theme.competition.table.input.bg} !important;
        }

        &.td-delete {
          max-width: 19px;
          border: 0 ! important;
          padding: 0 !important;
          // margin-left: -9px;
          svg {
            max-width: 17px;
          }
        }

        @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
          height: 36px;
        }
      }

      .td-dropdown {
        flex-flow: row;
        padding: 0 !important;
        height: 42px !important;
        width: 45px;

        .brg-input__control {
          background: inherit;
        }

        @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
          height: 36px;
        }
      }

      *:not(th) {
        font-size: 17px;
        line-height: 22px;
        font-weight: 400;
        letter-spacing: -0.02em;

        @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
          font-size: 13px;
          line-height: 16px;
        }
      }
    }

    ${TdTitle}, ${TdAuthor} {
      width: 100%;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
      width: calc(100vw - 20px);
    }
  }
`;

interface ShortListProjectsEditProps {
  competitionId: string;
  projectIds: string[];
  onSetProjects: (projectIds: string[]) => void;
  onSort?: (oldIndex: number, newIndex: number) => void;
  draggable?: boolean;
};

export const ShortListProjectsEdit: FC<ShortListProjectsEditProps> = ({
  competitionId,
  projectIds,
  onSetProjects,
  onSort
}) => {
  const { i18n, t } = useTranslation();
  const modal = useModal();
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
  const isTablet = useBreakpoint(LayoutConst.screen.md);

  const [items, setItems] = useState<Project[]>([]);

  useEffect(() => {
    if (projectIds.length) {
      API.request()
        .post()
        .endpoint(Endpoints.geograffee)
        .url(`api/project/GetProjectsList`)
        .body({
          projectIds: projectIds,
          projectFilter: 1,
          isLite: true,
          isContextIgnored: true
        })
        .send()
        .then(v => v.json())
        .then((v) => API.toLang<PageModel<Project>>(v, i18n.language))
        .then((v) => {
          if (v?.data?.length) {
            const prjs = [...v.data];
            prjs.sort((a: Project, b: Project): number => {
              const index_a = projectIds.indexOf(a.projectId);
              const index_b = projectIds.indexOf(b.projectId);

              if (index_a < index_b) {
                return -1;
              }
              if (index_a > index_b) {
                return 1;
              }
              // a must be equal to b
              return 0;
            });
            setItems(prjs);
          } else {
            setItems([]);
          }
        });
    } else {
      setItems([]);
    }
  }, [projectIds, i18n]);

  const tabsComponent = useMemo(() => (<CompetitionModalTabs/>), []);

  const onSelectClick = useCallback(() => {
    modal.show('select-short-list', {
      competitionId: competitionId,
      projectIds: projectIds,
      onSetProjects: onSetProjects,
      sortingMethods: [1, 2, 13],
      sendText: t('competitions_edit.btns.save'),
      searchData: { id: competitionId },
      modalType: MODAL_TYPE.finalist,
      tabs: tabsComponent
    });
  }, [modal, competitionId, projectIds, onSetProjects, tabsComponent, t]);

  const onDelete = useCallback((index: number, position?: number) => {
    const temp = [...projectIds];
    temp.splice(index, 1);
    onSetProjects(temp);
  }, [projectIds, onSetProjects])

  if (!projectIds?.length) {
    return (
      <DefaultButton onClick={onSelectClick} isCompetitionEdit hollow>{t('competition_edit.choose')}</DefaultButton>
    )
  }

  return (
    <FlexBox direction="column" directionMobile="column" gyMobile={12} gy={12}>
      <GlobalDragStyle/>
      <TableWrapper width={'100%'}>
        <thead>
        <tr>
          <th style={{ width: '45%' }} className='project-name'>
            Проект
          </th>
          <th style={{ width: '45%' }} className='author-name'>Автор</th>
          <th style={{ width: '10%' }} className='vertical-th jury-sum'>Балл</th>
          <th style={{ width: '10%' }} className='vertical-th jury-sum'></th>
        </tr>
        </thead>
        {
          <tbody className="short-list">
          {items?.map((value, idx) => (
            <tr key={value.projectId} className="short-list-item">
              <TdTitle>
                <div className="td-content">
                  <Link to={CreateLinkTo.projectNew(value)} target={"_blank"}>{(!!value?.title ? value.title : value?.additionalContent?.title?.text) as any}</Link>&nbsp;&nbsp;
                </div>
              </TdTitle>
              <TdAuthor>
                <div className="td-content">
                  <Link to={CreateLinkTo.designerNew(value.author)}
                        target={"_blank"}>{value.author.name + ' ' + value.author.surName}
                  </Link>&nbsp;&nbsp;
                </div>
              </TdAuthor>
              <TdSum>
                <div className="td-content" style={{ justifyContent: 'center' }}>
                  {value.competitonsGPA?.find(x => x.competitionId === competitionId)?.competitionGPA ?? '0'/*t('competition_edit.no_points')*/}
                </div>
              </TdSum>
              {!isMobile && (
                <TdDel style={{ width: '24px !important' }}>
                  <div className="td-content td-delete" style={{ cursor: 'pointer' }} onClick={() => onDelete(idx)}>
                    <DeleteIcon/>
                  </div>
                </TdDel>
              )}

            </tr>
          ))}
          </tbody>
        }

      </TableWrapper>
      {!!items?.length && <div>
        <DefaultButton style={{ marginBottom: isTablet ? '24px' : 0 }} isCompetitionEdit hollow onClick={onSelectClick}><PlusSvg/>{t('competition_edit.add_final_place')}
        </DefaultButton>
      </div>}
    </FlexBox>
  );
};